import Vue from "vue";
import moment from "moment";
import WaveLoader from "@/components/PulseInsights/WaveLoader.vue";
import PulseScoreGauge from "@/components/PulseInsights/PulseScoreGauge.vue";
import PulseTrendChart from "@/components/PulseInsights/PulseTrendChart.vue";
import { getPulseTrackTheme, getPulseBandStyle, hexToRgba, } from "@/components/PulseInsights/pulseTracks";
const CONTENT_TYPE_LABELS = {
    course: "Course",
    stand_alone_article: "Article",
};
// Recommended-action cards keyed by service slug. get-report returns only the
// slug in recommended_service.services[]; everything shown here — copy and the
// launch route — is owned by the FE. Launch routes mirror how the app already
// opens each service (Cara page, EAP page, DMH session booking).
const SERVICE_CARDS = {
    cara: {
        slug: "cara",
        headline: "Talk it through with Cara",
        description: "Your score is in a good place — talking with Cara is a great way to keep it that way. Cara is a friendly, confidential wellbeing guide who can help you work through everyday stress, stay on track, and build healthy habits that last.",
        bullets: [
            "Confidential, judgement-free conversations",
            "Practical guidance on stress, energy and focus",
            "Available anytime — no referral needed",
        ],
        cta: "Chat with Cara",
        route: "/cara",
    },
    eap: {
        slug: "eap",
        headline: "Confidential support is here for you",
        description: "Sometimes life gets heavy — work pressures, relationships, finances, or just the day-to-day grind. Our Employee Assistance Programme gives you free, confidential access to professionals who can help you work through whatever's on your mind.",
        bullets: [
            "Free and completely confidential",
            "Support for work, relationships, money and more",
            "Available 24/7 — by phone, online or in person",
        ],
        cta: "Access EAP Support",
        route: "/eap",
    },
    dmh: {
        slug: "dmh",
        headline: "A little extra support goes a long way",
        description: "Your mental wellbeing score suggests things have felt harder than usual lately. Our digital mental health service connects you with accredited counsellors who can offer real support, at a pace that works for you.",
        bullets: [
            "Accredited, professional counsellors",
            "Flexible sessions that fit around your life",
            "Confidential and easy to access",
        ],
        cta: "Connect with Mental Health Support",
        route: "/session-booking?service_type=sl_dmh",
    },
};
export default Vue.extend({
    name: "PulseReport",
    components: { WaveLoader, PulseScoreGauge, PulseTrendChart },
    data() {
        return {
            loading: true,
            // the report/trends currently on screen. Held locally (not read straight
            // off the store) so viewing a historical day via ?date= doesn't overwrite
            // the overview's cached "latest" report. The store is still written
            // through for the latest report only — see loadReport.
            report: null,
            trends: [],
            // a specific day was requested but has no completed check-in
            notFound: false,
            // which track's line the trend chart is focused on ("" = default to the
            // report's first track); set by clicking a score card
            selectedTrackSlug: "",
        };
    },
    computed: {
        // the day being viewed, from ?date=YYYY-MM-DD ("" = latest report)
        requestedDate() {
            const date = this.$route.query.date;
            return typeof date === "string" ? date : "";
        },
        // the completed-on day of the report on screen, for the header
        reportDateLabel() {
            return this.report && this.report.completed_on
                ? moment(this.report.completed_on).format("D MMM YYYY")
                : "";
        },
        // the track currently in focus — the clicked one, or the report's first
        effectiveSelectedSlug() {
            if (this.selectedTrackSlug)
                return this.selectedTrackSlug;
            return this.report && this.report.tracks.length
                ? this.report.tracks[0].track_slug
                : "";
        },
        // short key (mind | body | life) the trend chart emphasises
        selectedKey() {
            return getPulseTrackTheme(this.effectiveSelectedSlug).key;
        },
        // slugs of the tracks in this report — the only lines the chart draws
        availableTrackSlugs() {
            return this.report
                ? this.report.tracks.map((track) => track.track_slug)
                : [];
        },
        composite() {
            return this.report
                ? this.report.composite
                : {
                    band: null,
                    band_label: null,
                    blurb: null,
                    visible: true,
                    pulse_score: null,
                };
        },
        compositeVisible() {
            return this.composite.visible !== false;
        },
        compositeColor() {
            return getPulseBandStyle(this.composite.band).color;
        },
        markerPercent() {
            const score = this.composite.pulse_score;
            return score === null ? 0 : Math.max(0, Math.min(100, score));
        },
        // per-track narrative blocks, ranking order, honouring each track's visibility
        narrativeTracks() {
            if (!this.report)
                return [];
            return this.report.tracks.filter((track) => track.visible !== false && !!track.insight_blurb);
        },
        // resolve the recommended slug(s) to their FE card definitions; unknown
        // slugs are skipped rather than rendered empty
        serviceCards() {
            const slugs = this.report && this.report.recommended_service
                ? this.report.recommended_service.services || []
                : [];
            return slugs
                .map((slug) => SERVICE_CARDS[slug])
                .filter((card) => !!card);
        },
        contentItems() {
            const items = this.report ? this.report.content_items || [] : [];
            // drop items whose source record is missing (null title); P1 before P2
            return items
                .filter((item) => item.title)
                .sort((a, b) => a.priority > b.priority ? 1 : -1);
        },
        // composite change relative to the check-in *on screen* (not always the
        // latest): find the report's day in the trend series and diff it against
        // the previous scored day.
        compositeDelta() {
            const scored = this.trends
                .filter((point) => point.composite_score !== null)
                .slice()
                .sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
            if (scored.length < 2)
                return null;
            // completed_on is "YYYY-MM-DD HH:MM:SS"; the trend key is the day
            const reportDay = this.report && this.report.completed_on
                ? this.report.completed_on.slice(0, 10)
                : "";
            let currentIndex = scored.length - 1;
            if (reportDay) {
                const matched = scored.findIndex((point) => point.date === reportDay);
                if (matched !== -1)
                    currentIndex = matched;
            }
            // nothing before this check-in → no delta to show
            if (currentIndex < 1)
                return null;
            return (scored[currentIndex].composite_score -
                scored[currentIndex - 1].composite_score);
        },
    },
    mounted() {
        this.loadReport();
    },
    watch: {
        // clicking a point on the report's own trend chart pushes a new ?date=,
        // which the router keeps on this same component — reload for that day
        "$route.query.date"() {
            this.loadReport();
        },
    },
    methods: {
        themeFor(slug) {
            return getPulseTrackTheme(slug);
        },
        cardStyle(slug) {
            const theme = getPulseTrackTheme(slug);
            return {
                backgroundColor: hexToRgba(theme.color, 0.1),
                borderColor: hexToRgba(theme.color, 0.2),
            };
        },
        // focus the trend chart on this track
        selectTrack(slug) {
            this.selectedTrackSlug = slug;
        },
        // colour the selected score card's border/ring in the track's accent
        selectedCardStyle(slug) {
            const theme = getPulseTrackTheme(slug);
            return {
                borderColor: theme.color,
                boxShadow: `0 0 0 1px ${theme.color}`,
            };
        },
        bandBadgeStyle(bandSlug) {
            const style = getPulseBandStyle(bandSlug);
            return { color: style.color, backgroundColor: style.background };
        },
        contentTypeLabel(type) {
            return CONTENT_TYPE_LABELS[type] || "";
        },
        isContentClickable(item) {
            return item.type === "course" || item.type === "stand_alone_article";
        },
        goToContent(item) {
            if (item.type === "course") {
                this.$router.push(`/wellbeing/${item.id}`).catch(() => { });
            }
            else if (item.type === "stand_alone_article") {
                this.$router.push(`/wellbeing/article/${item.id}`).catch(() => { });
            }
        },
        goToService(card) {
            this.$router.push(card.route).catch(() => { });
        },
        goBack() {
            this.$router.push("/personal/insights").catch(() => { });
        },
        // clicking a point on the report's own trend chart re-points the report at
        // that day via the query string (the watcher above reloads)
        onTrendPointClick(date) {
            if (!date || date === this.requestedDate)
                return;
            this.$router
                .push({ path: "/personal/insights/report", query: { date } })
                .catch(() => { });
        },
        // drop ?date= to go back to the latest report
        viewLatest() {
            this.$router.push({ path: "/personal/insights/report" }).catch(() => { });
        },
        async loadReport() {
            const date = this.requestedDate;
            // viewing the latest report: paint the cached copy instantly (e.g.
            // arriving from the overview), then refresh. A specific day always
            // loads fresh — the cache holds the latest, not this day.
            if (!date && this.$store.getters.pulseReport) {
                this.report = this.$store.getters.pulseReport;
                this.loading = false;
            }
            else {
                this.loading = true;
            }
            this.notFound = false;
            const reportBody = {
                path: "api/u/pulse/get-report",
                noop: true,
            };
            if (date)
                reportBody.date = date;
            const [reportRes, trendsRes] = await Promise.all([
                this.api(reportBody),
                this.api({ path: "api/u/pulse/get-trends", noop: true }),
            ]);
            if (trendsRes && Array.isArray(trendsRes.trends)) {
                this.trends = trendsRes.trends;
                this.$store.commit("setPulseTrends", trendsRes.trends);
            }
            if (reportRes && reportRes.report) {
                this.report = reportRes.report;
                // keep the shared cache pointed at the latest report only
                if (!date)
                    this.$store.commit("setPulseReport", reportRes.report);
            }
            else {
                this.report = null;
                if (date) {
                    // that specific day has no completed check-in → empty state
                    this.notFound = true;
                }
                else {
                    // never completed a check-in at all → nothing to show
                    this.$router.push("/personal/insights").catch(() => { });
                }
            }
            this.loading = false;
        },
    },
});
